import { Check, FilterList } from '@mui/icons-material';
import { Chip, Divider, ListItemIcon, ListItemText, MenuItem, MenuList, Popover, Typography } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toolbarChipSx } from '@sophocles/data-grids/components/toolbar';
import { adminCommandMap, getCommandKeyFromDisplayName, userCommandMap } from '@sophocles/utils/commandUtils';

/**
 * Build deduplicated, sorted options from a command map.
 * Multiple command keys can share a displayName (e.g. STATUS_GET_SHORT / STATUS_GET_ZONEINFO both show "Get Status").
 * We group by displayName so the filter chip shows the human label, and match against all associated command keys.
 */
function buildOptions(commandMap) {
  const byDisplay = {};
  for (const [key, { displayName }] of Object.entries(commandMap)) {
    if (!byDisplay[displayName]) {
      byDisplay[displayName] = { displayName, commands: [] };
    }
    byDisplay[displayName].commands.push(key);
  }
  return Object.values(byDisplay).sort((a, b) => a.displayName.localeCompare(b.displayName));
}

const ADMIN_OPTIONS = buildOptions(adminCommandMap);
const USER_OPTIONS = buildOptions(userCommandMap);

/**
 * Compact command type filter matching the TimeRangeSelector chip style.
 *
 * @param {string|null} props.value - Currently selected displayName (null = all)
 * @param {(value: string|null) => void} props.onChange
 * @param {boolean} [props.disabled]
 */
export default function CommandTypeSelect({ value = null, onChange, disabled = false, isStaff = true }) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpen = useCallback(e => setAnchorEl(e.currentTarget), []);
  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleSelect = useCallback(
    val => {
      onChange(val);
      handleClose();
    },
    [onChange, handleClose],
  );

  const options = isStaff ? ADMIN_OPTIONS : USER_OPTIONS;

  const translatedOptions = useMemo(
    () =>
      options.map(opt => {
        const commandKey = getCommandKeyFromDisplayName(opt.displayName);
        const translated = t(commandKey);
        return {
          ...opt,
          label: translated === commandKey ? opt.displayName : translated,
        };
      }),
    [t, options],
  );

  const activeLabel =
    value == null
      ? t('commands.allCommands', { defaultValue: 'All Commands' })
      : translatedOptions.find(o => o.displayName === value)?.label || value;

  return (
    <>
      <Chip
        icon={<FilterList sx={{ fontSize: 16 }} />}
        label={activeLabel}
        size="small"
        variant="outlined"
        onClick={handleOpen}
        disabled={disabled}
        sx={toolbarChipSx}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, minWidth: 240, maxHeight: 400 } } }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5, pb: 0.5, color: 'text.secondary' }}>
          {t('commands.filterByType', { defaultValue: 'Filter by Type' })}
        </Typography>
        <MenuList dense>
          <MenuItem onClick={() => handleSelect(null)}>
            <ListItemIcon sx={{ minWidth: 28 }}>{value === null ? <Check fontSize="small" /> : null}</ListItemIcon>
            <ListItemText>{t('commands.allCommands', { defaultValue: 'All Commands' })}</ListItemText>
          </MenuItem>
          <Divider />
          {translatedOptions.map(opt => (
            <MenuItem key={opt.displayName} onClick={() => handleSelect(opt.displayName)}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                {value === opt.displayName ? <Check fontSize="small" /> : null}
              </ListItemIcon>
              <ListItemText>{opt.label}</ListItemText>
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </>
  );
}

/**
 * Client-side filter helper. Returns true if the row matches the selected command type filter.
 * @param {Object} row - Command row with `command` and `display_name` fields
 * @param {string|null} filterValue - Selected displayName from CommandTypeSelect (null = all)
 */
export function matchesCommandFilter(row, filterValue, isStaff = true) {
  if (filterValue == null) return true;
  const options = isStaff ? ADMIN_OPTIONS : USER_OPTIONS;
  const opt = options.find(o => o.displayName === filterValue);
  if (!opt) return true;
  return opt.commands.includes(row.command);
}
